// src/components/work/EditWorkModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Building2,
  CalendarDays,
  MapPin,
  Users,
  User as UserIcon,
  Info,
  Search,
  X,
  Plus,
  Clock,
  Tag as TagIcon,
} from "lucide-react";

import api from "../../api/axios";

/* =============================================================================
   THEME PRIMITIVES (kept identical to AddWorkModal)
============================================================================= */
const INPUT_BASE =
  "block w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-[0.95rem] leading-6 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";
const SELECT_BASE = INPUT_BASE + " pr-9";
const LABEL_BASE = "block text-[13px] font-medium text-gray-700";
const HINT_BASE = "text-[12px] text-gray-500";

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-gray-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-gray-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

function LabeledInput({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
  autoComplete = "off",
  rightAdornment = null,
  onKeyDown,
  onFocus,
}) {
  const hasRight = !!rightAdornment;
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={id || name} className={LABEL_BASE}>
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          autoComplete={autoComplete}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          className={[INPUT_BASE, hasRight ? "pr-10" : ""].join(" ")}
          required={required}
        />
        {hasRight ? (
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-gray-400">
            {rightAdornment}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LabeledSelect({
  id,
  name,
  label,
  value,
  onChange,
  children,
  required = false,
  disabled = false,
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id || name} className={LABEL_BASE}>
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <select
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        className={SELECT_BASE}
        disabled={disabled}
        required={required}
      >
        {children}
      </select>
    </div>
  );
}

/* =============================================================================
   UTILS (kept aligned with AddWorkModal)
============================================================================= */
const TYPES = ["task", "job", "appointment"];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const DEFAULT_TZ = "America/Toronto";

const STATUS_BY_TYPE = {
  task: ["todo", "in_progress", "blocked", "done", "cancelled"],
  job: [
    "new",
    "scheduled",
    "en_route",
    "in_progress",
    "completed",
    "invoiced",
    "cancelled",
    "no_show",
  ],
  appointment: [
    "planned",
    "confirmed",
    "in_progress",
    "done",
    "cancelled",
    "no_show",
  ],
};

function parseCsv(input = "") {
  return input
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function toISO(dtLocal) {
  if (!dtLocal) return undefined;
  try {
    const d = new Date(dtLocal);
    if (!Number.isFinite(d.getTime())) return undefined;
    return d.toISOString();
  } catch {
    return undefined;
  }
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "";
    // Convert to local "yyyy-MM-ddTHH:mm"
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch {
    return "";
  }
}

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function useDebouncedValue(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const parseList = (payload) =>
  Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
    ? payload
    : [];

const parseDetail = (payload) =>
  payload?.item || payload?.data?.item || payload?.data || payload || null;

/* =============================================================================
   CLIENT COMBOBOX  (enhanced: can be seeded with `initialClient`)
============================================================================= */
function ClientCombobox({
  selectedClientId,
  onSelectClientId,
  onSelectClientObject, // for address sync
  disabled,
  initialClient,        // NEW: object { _id/id, name, email, address? }
}) {
  const [clientQuery, setClientQuery] = useState("");
  const [clientOpen, setClientOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const [selectedClient, setSelectedClient] = useState(null);
  const [suppressAutoResolve, setSuppressAutoResolve] = useState(false);

  // Seed the combobox with the initial client object (from Edit modal)
  useEffect(() => {
    if (!initialClient) return;
    const id = String(initialClient._id || initialClient.id || "");
    if (!id) return;
    setSelectedClient(initialClient);
    // show chip; keep input empty (so it reads like a search box)
    setClientQuery(""); 
  }, [initialClient]);

  const debounced = useDebouncedValue(clientQuery, 250);
  const rootRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setClientOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const { data, isFetching, isError } = useQuery({
    queryKey: ["clients", debounced],
    queryFn: async () => {
      const params = debounced ? { search: debounced } : {};
      const res = await api.get("/clients", { params });
      return parseList(res.data);
    },
    enabled: clientOpen,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!clientOpen) return;
    if (suppressAutoResolve) return;
    if (selectedClientId) return;

    const list = Array.isArray(data) ? data : [];
    const q = debounced.trim().toLowerCase();
    if (!q || list.length === 0) return;

    const exact =
      list.find(
        (c) =>
          (c.name && c.name.toLowerCase() === q) ||
          (c.email && c.email.toLowerCase() === q)
      ) || null;

    if (exact) {
      const id = String(exact._id || exact.id || "");
      onSelectClientId(id);
      setSelectedClient(exact);
      onSelectClientObject?.(exact);
      setClientOpen(false);
      setSuppressAutoResolve(true);
    } else if (list.length === 1) {
      const only = list[0];
      const id = String(only._id || only.id || "");
      onSelectClientId(id);
      setSelectedClient(only);
      onSelectClientObject?.(only);
      setClientOpen(false);
      setSuppressAutoResolve(true);
    }
  }, [
    clientOpen,
    suppressAutoResolve,
    selectedClientId,
    data,
    debounced,
    onSelectClientId,
    onSelectClientObject,
  ]);

  useEffect(() => {
    const display =
      selectedClient?.name || selectedClient?.email || selectedClientId || "";
    if (!display) return;
    if (clientQuery && clientQuery !== display) {
      if (selectedClientId) onSelectClientId("");
      if (selectedClient) setSelectedClient(null);
      setSuppressAutoResolve(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientQuery]);

  const list = Array.isArray(data) ? data : [];

  return (
    <div ref={rootRef} className="relative">
      <div className="space-y-1.5">
        <label className={LABEL_BASE}>Client</label>
        <div
          className={cx(
            "flex items-center gap-2 rounded-lg border px-3.5 py-2.5",
            disabled ? "bg-gray-50" : "bg-white"
          )}
        >
          <Search className="text-gray-400" size={16} />
          <input
            value={clientQuery}
            onChange={(e) => {
              setClientQuery(e.target.value);
              setClientOpen(true);
              setHighlightIdx(0);
            }}
            onFocus={() => setClientOpen(true)}
            placeholder="Search client by name or email"
            disabled={disabled}
            className="w-full border-0 p-0 text-[0.95rem] outline-none placeholder:text-gray-400"
          />
          {(selectedClientId || clientQuery) && !disabled ? (
            <button
              type="button"
              aria-label="Clear client"
              className="rounded-full p-1 text-gray-600 hover:bg-gray-100"
              onClick={() => {
                setClientQuery("");
                setClientOpen(false);
                onSelectClientId("");
                setSelectedClient(null);
                onSelectClientObject?.(null);
                setSuppressAutoResolve(false);
              }}
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        {selectedClient ? (
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-100">
              {selectedClient.name || selectedClient.email || "Selected client"}
              <button
                type="button"
                className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
                onClick={() => {
                  onSelectClientId("");
                  setSelectedClient(null);
                  onSelectClientObject?.(null);
                  setSuppressAutoResolve(false);
                }}
                aria-label="Remove selected client"
                title="Remove"
              >
                ✕
              </button>
            </span>
          </div>
        ) : null}
      </div>

      {clientOpen && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          {isError ? (
            <div className="px-3 py-3 text-sm text-red-600">Failed to load clients.</div>
          ) : isFetching ? (
            <div className="px-3 py-3 text-sm text-gray-500">Loading…</div>
          ) : list.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">No results</div>
          ) : (
            <ul className="max-h-64 overflow-auto py-1">
              {list.map((c, idx) => {
                const id = String(c._id || c.id || "");
                const active = idx === highlightIdx;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={cx(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50",
                        active ? "bg-blue-50" : ""
                      )}
                      onMouseEnter={() => setHighlightIdx(idx)}
                      onClick={() => {
                        onSelectClientId(id);
                        setSelectedClient(c);
                        onSelectClientObject?.(c);
                        setClientOpen(false);
                        setSuppressAutoResolve(true);
                        setClientQuery(c.name || c.email || id);
                      }}
                    >
                      <div className="grid h-7 w-7 place-items-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-700">
                        {initials(c.name || c.email || "?")}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-gray-900">
                          {c.name || "(No name)"}
                        </div>
                        {c.email ? (
                          <div className="truncate text-xs text-gray-500">
                            {c.email}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   USERS MULTI-SELECT  (enhanced: can be seeded with `seedUsers`)
============================================================================= */
function UsersMultiSelect({ value = [], onChange, disabled, seedUsers = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const debounced = useDebouncedValue(q, 200);
  const rootRef = useRef(null);

  const [selectedUsers, setSelectedUsers] = useState([]); // [{id,name,email,role}]
  const selectedSet = new Set(value);

  // Seed from parent when Edit modal loads users by IDs
  useEffect(() => {
    if (Array.isArray(seedUsers) && seedUsers.length) {
      setSelectedUsers(seedUsers);
    }
  }, [seedUsers]);

  useEffect(() => {
    function handleOutside(e) {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const { data, isFetching, isError } = useQuery({
    queryKey: ["users", debounced],
    queryFn: async () => {
      const params = debounced ? { search: debounced } : {};
      const res = await api.get("/users", { params });
      return Array.isArray(res.data) ? res.data : res.data?.items || [];
    },
    enabled: open,
    staleTime: 60_000,
  });

  const list = Array.isArray(data) ? data : [];

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) {
      next.delete(id);
      setSelectedUsers((prev) => prev.filter((u) => (u._id || u.id) !== id));
    } else {
      next.add(id);
      const obj =
        list.find((u) => String(u._id || u.id) === id) || { id, _id: id };
      setSelectedUsers((prev) => {
        const exists = prev.some((u) => String(u._id || u.id) === id);
        return exists ? prev : [...prev, obj];
      });
    }
    onChange(Array.from(next));
  };

  useEffect(() => {
    if (!list.length || !selectedUsers.length) return;
    setSelectedUsers((prev) =>
      prev.map((u) => {
        const id = String(u._id || u.id);
        const richer = list.find((x) => String(x._id || x.id) === id);
        return richer || u;
      })
    );
  }, [list, selectedUsers.length]);

  return (
    <div ref={rootRef} className="relative">
      <div className="space-y-1.5">
        <label className={LABEL_BASE}>Assignees</label>
        <div
          className={cx(
            "flex items-center gap-2 rounded-lg border px-3.5 py-2.5",
            disabled ? "bg-gray-50" : "bg-white"
          )}
        >
          <Search size={16} className="text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder="Search user by name or email"
            disabled={disabled}
            className="w-full border-0 p-0 text-[0.95rem] outline-none placeholder:text-gray-400"
          />
        </div>

        {value.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {value.map((id) => {
              const u = selectedUsers.find(
                (x) => String(x._id || x.id) === String(id)
              );
              const label = u?.name || u?.email || id;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-100"
                >
                  {label}
                  <button
                    type="button"
                    className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
                    onClick={() => toggle(String(id))}
                    aria-label="Remove"
                    title="Remove"
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
        ) : null}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          {isError ? (
            <div className="px-3 py-3 text-sm text-red-600">Failed to load users.</div>
          ) : isFetching ? (
            <div className="px-3 py-3 text-sm text-gray-500">Loading…</div>
          ) : list.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">No results</div>
          ) : (
            <ul className="max-h-64 overflow-auto py-1">
              {list.map((u) => {
                const id = String(u._id || u.id || "");
                const checked = selectedSet.has(id);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={cx(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50",
                        checked ? "bg-blue-50" : ""
                      )}
                      onClick={() => toggle(id)}
                    >
                      <div className="grid h-7 w-7 place-items-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-700">
                        {initials(u.name || u.email || "?")}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-gray-900">
                          {u.name || "(No name)"} {u.role ? `(${u.role})` : ""}
                        </div>
                        {u.email ? (
                          <div className="truncate text-xs text-gray-500">
                            {u.email}
                          </div>
                        ) : null}
                      </div>
                      <div className="ml-auto">
                        <input
                          type="checkbox"
                          readOnly
                          checked={checked}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   TAG INPUT (chips)
============================================================================= */
function TagsInput({ value = [], onChange }) {
  const [buf, setBuf] = useState("");
  const add = () => {
    const s = buf.trim();
    if (!s) return;
    if (!value.includes(s)) onChange([...value, s]);
    setBuf("");
  };
  const remove = (t) => onChange(value.filter((x) => x !== t));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-100"
          >
            {t}
            <button
              type="button"
              onClick={() => remove(t)}
              className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
              aria-label={`Remove ${t}`}
              title="Remove"
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={buf}
          onChange={(e) => setBuf(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add a tag and press Enter"
          className={INPUT_BASE + " flex-1"}
        />
        <button
          type="button"
          onClick={add}
          className="px-3.5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 shadow-sm"
        >
          Add
        </button>
      </div>
    </div>
  );
}

/* =============================================================================
   MAIN: EditWorkModal
============================================================================= */
export default function EditWorkModal({
  open,
  onClose,
  workId,          // required
  onUpdated,       // (updatedWork) => void
}) {
  const formRef = useRef(null);

  // ---------- Load existing work ----------
  const {
    data: work,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["work", workId],
    queryFn: async () => {
      const res = await api.get(`/works/${workId}`);
      return parseDetail(res.data) || {};
    },
    enabled: open && !!workId,
    staleTime: 30_000,
  });

  // ---------- Local state ----------
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Core fields
  const [type, setType] = useState("task"); // locked (disabled)
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [status, setStatus] = useState(STATUS_BY_TYPE["task"][0]);

  // Client & assignees
  const [clientId, setClientId] = useState("");
  const [selectedClientObj, setSelectedClientObj] = useState(null);
  const [clientSeed, setClientSeed] = useState(null);       // NEW: for ClientCombobox
  const [assignees, setAssignees] = useState([]);
  const [assigneeSeeds, setAssigneeSeeds] = useState([]);   // NEW: for UsersMultiSelect
  const [tags, setTags] = useState([]);

  // Address (no geo)
  const [keepAddrSynced, setKeepAddrSynced] = useState(false);
  const [addrStreet, setAddrStreet] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrPostal, setAddrPostal] = useState("");
  const [addrCountry, setAddrCountry] = useState("");
  const [addrRaw, setAddrRaw] = useState("");

  // Schedule
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [timezone, setTimezone] = useState(DEFAULT_TZ);
  const [rrule, setRRule] = useState("");
  const [exdates, setExdates] = useState("");

  // Type-specific
  const [checklistBuf, setChecklistBuf] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [serviceType, setServiceType] = useState("");
  const [expectedAmount, setExpectedAmount] = useState("");
  const [onsiteRequired, setOnsiteRequired] = useState(true);
  const [apptConfirmed, setApptConfirmed] = useState(false);
  const [routeFromLabel, setRouteFromLabel] = useState("");
  const [routeMinutes, setRouteMinutes] = useState("");

  // Hydrate local state when work loads
  useEffect(() => {
    if (!work || !open) return;
    const w = work;

    const t = w.type || "task";
    setType(t);

    setTitle(w.title || "");
    setDescription(w.description || "");
    setPriority(w.priority || "normal");

    const stList = STATUS_BY_TYPE[t] || STATUS_BY_TYPE["task"];
    setStatus(stList.includes(w.status) ? w.status : stList[0]);

    setClientId(w.clientId || "");
    setAssignees(Array.isArray(w.assignees) ? w.assignees.map(String) : []);
    setTags(Array.isArray(w.tags) ? w.tags : []);

    const a = w.address || {};
    setAddrStreet(a.street || "");
    setAddrCity(a.city || "");
    setAddrState(a.state || "");
    setAddrPostal(a.postalCode || a.zip || "");
    setAddrCountry(a.country || "");
    setAddrRaw(
      a.raw ||
        [a.street, a.city, a.state, a.postalCode || a.zip, a.country]
          .filter(Boolean)
          .join(", ")
    );

    const s = w.schedule || {};
    setStartLocal(toLocalInputValue(s.start));
    setEndLocal(toLocalInputValue(s.end));
    setAllDay(!!s.allDay);
    setTimezone(s.timezone || DEFAULT_TZ);
    setRRule(s.rrule || "");
    const ex = Array.isArray(s.exdates) ? s.exdates : [];
    const formatYMD = (iso) => {
      const d = new Date(iso);
      if (!Number.isFinite(d.getTime())) return null;
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    setExdates(ex.map(formatYMD).filter(Boolean).join(", "));

    // Profiles
    if (t === "task") {
      const tp = w.taskProfile || {};
      const list = Array.isArray(tp.checklist) ? tp.checklist : [];
      setChecklist(
        list.map((c) => ({
          id:
            String(c._id || c.id) ||
            (crypto.randomUUID?.() || String(Date.now())),
          label: c.label,
          done: !!c.done,
        }))
      );
    } else if (t === "job") {
      const jp = w.jobProfile || {};
      setServiceType(jp.serviceType || "");
      setOnsiteRequired(jp.onsiteRequired !== false);
      const amt = jp?.pricing?.expectedAmount;
      setExpectedAmount(Number.isFinite(amt) ? String(amt) : "");
    } else if (t === "appointment") {
      const ap = w.appointmentProfile || {};
      setApptConfirmed(!!ap.confirmed);
      setRouteFromLabel(ap?.route?.fromLabel || "");
      setRouteMinutes(
        Number.isFinite(ap?.route?.travelMinutes)
          ? String(ap.route.travelMinutes)
          : ""
      );
    }

    setFormError("");
  }, [work, open]);

  // -------- Hydrate display objects for Client & Assignees (NEW) --------
  // Fetch client object for chip display
  useEffect(() => {
    let stop = false;
    async function run() {
      if (!open) return;
      if (!clientId) {
        setClientSeed(null);
        return;
      }
      try {
        const res = await api.get(`/clients/${clientId}`);
        const obj = parseDetail(res.data);
        if (!stop) {
          setClientSeed(obj || null);
          setSelectedClientObj(obj || null); // also feeds address sync
        }
      } catch {
        if (!stop) {
          setClientSeed(null);
        }
      }
    }
    run();
    return () => {
      stop = true;
    };
  }, [open, clientId]);

  // Fetch users for the assignee chips
  useEffect(() => {
    let stop = false;
    async function fetchUsersByIds(ids = []) {
      if (!ids.length || !open) {
        setAssigneeSeeds([]);
        return;
      }
      // Try a batch endpoint first; fall back to N detail calls.
      try {
        const res = await api.get("/users", { params: { ids: ids.join(",") } });
        const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
        if (list.length && !stop) {
          setAssigneeSeeds(list);
          return;
        }
      } catch (_) {}
      try {
        const out = await Promise.all(
          ids.map(async (id) => {
            try {
              const r = await api.get(`/users/${id}`);
              return parseDetail(r.data);
            } catch {
              return { id, _id: id };
            }
          })
        );
        if (!stop) setAssigneeSeeds(out.filter(Boolean));
      } catch {
        if (!stop) setAssigneeSeeds([]);
      }
    }
    fetchUsersByIds(assignees);
    return () => {
      stop = true;
    };
  }, [open, assignees]);

  // ---------- Build payload ----------
  const buildPayload = useCallback(() => {
    if (!title.trim()) throw new Error("Title is required.");
    if (type === "job" && !clientId) throw new Error("Client is required for jobs.");
    if (type === "appointment" && (!startLocal || (!clientId && !work?.parentWorkId))) {
      throw new Error("Appointment requires a start time and either a client or parent work.");
    }
    if (startLocal && endLocal && new Date(endLocal) < new Date(startLocal)) {
      throw new Error("End time must be after start time.");
    }

    const payload = {
      title: title.trim(),
      description: description.trim() || undefined,
      status,
      priority,
      clientId: clientId || null,
      assignees: Array.isArray(assignees) ? assignees : [],
      tags,
    };

    const hasAddress =
      addrStreet || addrCity || addrState || addrPostal || addrCountry || addrRaw;
    payload.address = hasAddress
      ? {
          street: addrStreet || undefined,
          city: addrCity || undefined,
          state: addrState || undefined,
          postalCode: addrPostal || undefined,
          country: addrCountry || undefined,
          raw: addrRaw || undefined,
        }
      : undefined;

    const sch = {};
    const sISO = toISO(startLocal);
    const eISO = toISO(endLocal);
    if (sISO) sch.start = sISO;
    if (eISO) sch.end = eISO;
    if (allDay) sch.allDay = true;
    sch.timezone = timezone || DEFAULT_TZ;
    if (rrule) sch.rrule = rrule;
    const ex = parseCsv(exdates)
      .map((d) => {
        const dt = new Date(d);
        return Number.isFinite(dt.getTime()) ? dt.toISOString() : null;
      })
      .filter(Boolean);
    if (ex.length) sch.exdates = ex;
    payload.schedule = Object.keys(sch).length ? sch : undefined;

    if (type === "task") {
      if (checklist.length) {
        payload.taskProfile = {
          checklist: checklist.map((c) => ({ label: c.label, done: !!c.done })),
        };
      } else {
        payload.taskProfile = { checklist: [] };
      }
    } else if (type === "job") {
      const amt = expectedAmount
        ? Number(String(expectedAmount).replace(/[^0-9.-]/g, ""))
        : undefined;
      payload.jobProfile = {
        serviceType: serviceType || undefined,
        onsiteRequired: !!onsiteRequired,
        pricing: Number.isFinite(amt) ? { expectedAmount: amt } : undefined,
      };
    } else if (type === "appointment") {
      payload.appointmentProfile = {
        confirmed: !!apptConfirmed,
        route: {
          fromLabel: routeFromLabel || undefined,
          travelMinutes: routeMinutes ? Number(routeMinutes) : undefined,
        },
      };
    }

    return payload;
  }, [
    title,
    description,
    status,
    priority,
    clientId,
    work?.parentWorkId,
    assignees,
    tags,
    addrStreet,
    addrCity,
    addrState,
    addrPostal,
    addrCountry,
    addrRaw,
    startLocal,
    endLocal,
    allDay,
    timezone,
    rrule,
    exdates,
    type,
    checklist,
    serviceType,
    expectedAmount,
    onsiteRequired,
    apptConfirmed,
    routeFromLabel,
    routeMinutes,
  ]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      setSaving(true);
      const payload = buildPayload();
      const res = await api.patch(`/works/${workId}`, payload);
      const updated = parseDetail(res.data) || payload;
      toast.success("Work updated");
      onUpdated?.(updated);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to update work";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addChecklist = () => {
    const label = checklistBuf.trim();
    if (!label) return;
    setChecklist((prev) => [
      ...prev,
      { id: crypto.randomUUID?.() || String(Date.now()), label, done: false },
    ]);
    setChecklistBuf("");
  };
  const removeChecklist = (id) =>
    setChecklist((prev) => prev.filter((c) => c.id !== id));

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative bg-white w-full max-w-6xl rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-2xl">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <Building2 size={32} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold truncate">Edit Work</h2>
              <p className="text-sm text-blue-100">
                Update a task, job, or appointment.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-full p-2"
            type="button"
            aria-label="Close"
            disabled={saving}
          >
            <X />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50/50">
          {isLoading ? (
            <div className="text-sm text-gray-600">Loading…</div>
          ) : isError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error?.message || "Failed to load work"}
            </div>
          ) : (
            <form ref={formRef} onSubmit={submit} className="space-y-8">
              <div className="space-y-6">
                {/* Basic */}
                <Section icon={Info} title="Basic Information" hint="Core details for this work item">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <LabeledSelect label="Type" value={type} onChange={() => {}} disabled>
                      {TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t[0].toUpperCase() + t.slice(1)}
                        </option>
                      ))}
                    </LabeledSelect>

                    <LabeledSelect
                      label="Priority"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                    >
                      {PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {p[0].toUpperCase() + p.slice(1)}
                        </option>
                      ))}
                    </LabeledSelect>

                    <LabeledSelect
                      label="Status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {(STATUS_BY_TYPE[type] || STATUS_BY_TYPE["task"]).map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </option>
                      ))}
                    </LabeledSelect>

                    <LabeledInput
                      label="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Install new HVAC unit"
                      required
                      autoComplete="off"
                    />
                    <div className="md:col-span-2">
                      <label className={LABEL_BASE}>Description</label>
                      <textarea
                        rows={2}
                        className={INPUT_BASE + " h-auto"}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional details…"
                      />
                    </div>
                  </div>
                </Section>

                {/* Client + Assignees */}
                <Section
                  icon={Users}
                  title="Client & Assignment"
                  hint="Search a client and choose team members"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ClientCombobox
                      selectedClientId={clientId}
                      onSelectClientId={setClientId}
                      onSelectClientObject={setSelectedClientObj}
                      disabled={saving}
                      initialClient={clientSeed}     // << seed for display
                    />
                    <UsersMultiSelect
                      value={assignees}
                      onChange={setAssignees}
                      disabled={saving}
                      seedUsers={assigneeSeeds}       // << seed for display
                    />
                  </div>
                </Section>

                {/* Schedule */}
                <Section
                  icon={CalendarDays}
                  title={type === "appointment" ? "Schedule (required for appointments)" : "Schedule (optional)"}
                  hint="Set the time window, timezone, and recurrence (if any)"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <LabeledInput
                      label={
                        <>
                          Start {type === "appointment" ? <span className="text-red-500">*</span> : null}
                        </>
                      }
                      type="datetime-local"
                      value={startLocal}
                      onChange={(e) => setStartLocal(e.target.value)}
                      required={type === "appointment"}
                      rightAdornment={<Clock size={16} />}
                    />
                    <LabeledInput
                      label="End"
                      type="datetime-local"
                      value={endLocal}
                      onChange={(e) => setEndLocal(e.target.value)}
                      rightAdornment={<Clock size={16} />}
                    />

                    <div className="flex items-center gap-3">
                      <input
                        id="allDay"
                        type="checkbox"
                        checked={allDay}
                        onChange={(e) => setAllDay(e.target.checked)}
                      />
                      <label htmlFor="allDay" className="text-[13px] text-gray-700">
                        All day
                      </label>
                    </div>

                    <LabeledInput
                      label="Timezone"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      placeholder={DEFAULT_TZ}
                    />

                    <LabeledInput
                      label="RRULE"
                      value={rrule}
                      onChange={(e) => setRRule(e.target.value)}
                      placeholder="e.g., FREQ=WEEKLY;BYDAY=MO,WE"
                    />
                    <LabeledInput
                      label="Exception Dates (CSV)"
                      value={exdates}
                      onChange={(e) => setExdates(e.target.value)}
                      placeholder="yyyy-MM-dd, yyyy-MM-dd"
                    />
                  </div>
                  {type === "appointment" && !startLocal ? (
                    <div className="mt-2 text-[12px] text-red-600">Appointment requires a start date/time.</div>
                  ) : null}
                  {startLocal && endLocal && new Date(endLocal) < new Date(startLocal) ? (
                    <div className="mt-2 text-[12px] text-red-600">End must be after start.</div>
                  ) : null}
                </Section>

                {/* Address */}
                <Section icon={MapPin} title="Address" hint="Prefill from the selected client or edit as needed">
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      id="syncAddr"
                      type="checkbox"
                      checked={keepAddrSynced}
                      onChange={(e) => setKeepAddrSynced(e.target.checked)}
                    />
                    <label htmlFor="syncAddr" className="text-[13px] text-gray-700">
                      Keep address synced with client
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <LabeledInput
                      label="Street"
                      value={addrStreet}
                      onChange={(e) => {
                        setKeepAddrSynced(false);
                        setAddrStreet(e.target.value);
                      }}
                      disabled={keepAddrSynced}
                    />
                    <LabeledInput
                      label="City"
                      value={addrCity}
                      onChange={(e) => {
                        setKeepAddrSynced(false);
                        setAddrCity(e.target.value);
                      }}
                      disabled={keepAddrSynced}
                    />
                    <LabeledInput
                      label="State/Province"
                      value={addrState}
                      onChange={(e) => {
                        setKeepAddrSynced(false);
                        setAddrState(e.target.value);
                      }}
                      disabled={keepAddrSynced}
                    />
                    <LabeledInput
                      label="ZIP/Postal"
                      value={addrPostal}
                      onChange={(e) => {
                        setKeepAddrSynced(false);
                        setAddrPostal(e.target.value);
                      }}
                      disabled={keepAddrSynced}
                    />
                    <LabeledInput
                      label="Country"
                      value={addrCountry}
                      onChange={(e) => {
                        setKeepAddrSynced(false);
                        setAddrCountry(e.target.value);
                      }}
                      disabled={keepAddrSynced}
                    />
                    <LabeledInput
                      label="Raw (optional)"
                      value={addrRaw}
                      onChange={(e) => {
                        setKeepAddrSynced(false);
                        setAddrRaw(e.target.value);
                      }}
                      disabled={keepAddrSynced}
                    />
                  </div>
                </Section>

                {/* Type-specific */}
                {type === "task" ? (
                  <Section icon={UserIcon} title="Task Checklist" hint="Track steps to complete the task">
                    <div className="flex items-center gap-2">
                      <input
                        value={checklistBuf}
                        onChange={(e) => setChecklistBuf(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addChecklist();
                          }
                        }}
                        placeholder="Add checklist item"
                        className={INPUT_BASE + " flex-1"}
                      />
                      <button
                        type="button"
                        onClick={addChecklist}
                        className="px-3.5 py-2.5 rounded-lg border text-sm hover:bg-slate-50"
                      >
                        <Plus size={16} className="inline -mt-1 mr-1" />
                        Add
                      </button>
                    </div>
                    {checklist.length > 0 ? (
                      <ul className="mt-3 space-y-2">
                        {checklist.map((c) => (
                          <li
                            key={c.id}
                            className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                          >
                            <span>{c.label}</span>
                            <button
                              type="button"
                              onClick={() => removeChecklist(c.id)}
                              className="rounded-md p-1 text-gray-600 hover:bg-gray-100"
                            >
                              <X size={16} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </Section>
                ) : null}

                {type === "job" ? (
                  <Section icon={Building2} title="Job Details" hint="Service and pricing information">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <LabeledInput
                        label="Service Type"
                        value={serviceType}
                        onChange={(e) => setServiceType(e.target.value)}
                        placeholder="e.g., Installation"
                      />
                      <LabeledInput
                        label="Expected Amount"
                        value={expectedAmount}
                        onChange={(e) => setExpectedAmount(e.target.value)}
                        placeholder="e.g., 1200"
                        type="text"
                      />
                      <div className="flex items-center gap-3 pt-7">
                        <input
                          id="onsiteReq"
                          type="checkbox"
                          checked={onsiteRequired}
                          onChange={(e) => setOnsiteRequired(e.target.checked)}
                        />
                        <label htmlFor="onsiteReq" className="text-[13px] text-gray-700">
                          Onsite required
                        </label>
                      </div>
                    </div>
                  </Section>
                ) : null}

                {type === "appointment" ? (
                  <Section icon={CalendarDays} title="Appointment Details" hint="Confirmation and travel">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-3">
                        <input
                          id="apptConfirmed"
                          type="checkbox"
                          checked={apptConfirmed}
                          onChange={(e) => setApptConfirmed(e.target.checked)}
                        />
                        <label htmlFor="apptConfirmed" className="text-[13px] text-gray-700">
                          Confirmed
                        </label>
                      </div>
                      <LabeledInput
                        label="Route From (label)"
                        value={routeFromLabel}
                        onChange={(e) => setRouteFromLabel(e.target.value)}
                        placeholder="e.g., Warehouse"
                      />
                      <LabeledInput
                        label="Travel Minutes"
                        value={routeMinutes}
                        onChange={(e) => setRouteMinutes(e.target.value)}
                        type="number"
                        placeholder="e.g., 35"
                      />
                    </div>
                  </Section>
                ) : null}

                {/* Tags */}
                <Section icon={TagIcon} title="Tags" hint="Organize and filter work">
                  <TagsInput value={tags} onChange={setTags} />
                </Section>

                {/* Guidance / Errors */}
                {formError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {formError}
                  </div>
                ) : (
                  <div className={HINT_BASE + " flex items-center gap-2"}>
                    <Info size={14} className="text-gray-400" />
                    Jobs require a client. Appointments require a start time and either a client or a
                    parent work item.
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="px-8 py-4 bg-white border-t flex justify-end gap-3 shadow-lg rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-100 border border-gray-200"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            className={cx(
              "px-7 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 shadow-sm",
              saving ? "opacity-60 cursor-not-allowed" : ""
            )}
            disabled={saving || isLoading || isError}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
